import { useEffect, useState } from "react";
import { usersRepo, type AdminUserRow } from "@/services/users.repository";
import type { TenantKey } from "@/domain/space";

/**
 * 사용자 목록(관리용) 구독. rules상 admin만 전체 조회 가능.
 * @param enabled false면 구독하지 않음(권한 없는 화면에서 permission-denied 회피). 기본 true.
 */
export function useUsers(enabled = true) {
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = usersRepo.watch(
      (r) => { setRows(r); setLoading(false); },
      (e) => { setError(e); setLoading(false); },
    );
    return unsub;
  }, [enabled]);

  return { rows, loading, error };
}

/**
 * 한 법인으로 들어온 가입 신청(가입 대기) 구독 — 법인 관리자용(rules 의 pendingSignupReadable 과 같은 where).
 * enabled=false 면 구독하지 않음(권한 없는 사람 permission-denied 회피).
 */
export function usePendingSignups(tenant: TenantKey, enabled = true) {
  // 구독 키별 결과 — 법인이 바뀌면 이전 법인 결과를 보이지 않게(키가 다르면 "불러오는 중")
  const key = enabled ? tenant : null;
  const [state, setState] = useState<{ key: TenantKey | null; rows: AdminUserRow[]; error: Error | null }>({
    key: null, rows: [], error: null,
  });

  useEffect(() => {
    if (!key) return;
    return usersRepo.watchPendingSignups(
      key,
      (rows) => setState({ key, rows, error: null }),
      (error) => setState({ key, rows: [], error }),
    );
  }, [key]);

  const fresh = key !== null && state.key === key;
  return { rows: fresh ? state.rows : [], loaded: key === null || fresh, error: fresh ? state.error : null };
}
